import React, { useMemo, useRef, useEffect } from "react";
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  PieSeries,
  AccumulationTooltip,
  AccumulationLegend,
  Inject,
} from "@syncfusion/ej2-react-charts";

import WorldMap from "./WorldMap";
import CityMap from "./CityMap";
import LocationFlipCard from "./LocationFlipCard";
import { formatPercent } from "../../utils/formatNumber";
import "./Location.css";

const PIE_COLORS = [
  "#3366CC", "#DC3912", "#FF9900", "#109618",
  "#990099", "#0099C6", "#DD4477", "#66AA00",
  "#B82E2E", "#316395", "#994499", "#22AA99",
  "#AAAA11", "#6633CC", "#E67300",
];

const displayCountry = (name) => {
  if (name === "Turkey") return "Türkiye";
  return name;
};

/* ------------------------------
   SKELETON
--------------------------------*/
function ChartSkeleton() {
  return <div className="location-chart-skeleton" />;
}

export default function LocationCharts({
  countries = [],
  cities = [],
  mapCities = [],
  selectedCountry,
  onCountrySelect,
  loading = false,
  refreshKey,
}) {
return (
  <div className="location-charts-block">
    <LocationFlipCard

      /* ================= FRONT (PIES) ================= */
      front={({ setFlipped }) => (
        <div className="location-grid">

          {/* Countries pie */}
          <div className="location-box location-chart-box">
            <div className="location-box-header">
              <h4>Countries</h4>
            </div>

            {loading ? (
              <ChartSkeleton />
            ) : (
              <LocationPie 
                data={countries} 
                onPointClick={(country) => onCountrySelect(country)}
                refreshKey={refreshKey}
              />
            )}
          </div>

          {/* Cities pie */}
          <div className="location-box location-chart-box location-map-anchor">
            <button
              className="location-map-btn"
              onClick={() => setFlipped(true)}
            >
              Show map
            </button>

            <div className="location-box-header">
              <h4>
                {selectedCountry
                  ? `${displayCountry(selectedCountry)} – Cities`
                  : "All Cities"}
              </h4>
            </div>


            {loading ? (
              <ChartSkeleton />
            ) : (
              <LocationPieInner data={cities} refreshKey={refreshKey} />
            )}
          </div>
        </div>
      )}

      /* ================= BACK (MAPS) ================= */
      back={({ setFlipped }) => (
        <div className="location-grid">

          {/* World map */}
          <div className="location-box location-chart-box">
            <div className="location-box-header">
              <h4>World Map</h4>
            </div>

            {loading ? (
              <ChartSkeleton />
            ) : !countries.length ? (
              <div className="location-empty">No data</div>
            ) : (
              <WorldMap
                countries={countries}
                selectedCountry={selectedCountry}
                onCountrySelect={onCountrySelect}
              />
            )}
          </div>

          {/* City map */}
          <div className="location-box location-chart-box location-map-anchor">
            <button
              className="location-map-btn"
              onClick={() => setFlipped(false)}
            >
              Show charts
            </button>

            <div className="location-box-header">
              <h4>
                {selectedCountry
                  ? `${displayCountry(selectedCountry)} – Cities`
                  : "City Map"}
              </h4>
            </div>

            {loading ? (
              <ChartSkeleton />
            ) : !selectedCountry ? (
              <div className="location-empty">
                Select a country from world map
              </div>
            ) : mapCities.length === 0 ? (
              <div className="location-empty">
                No city data for this country
              </div>
            ) : (
              <CityMap
                country={selectedCountry}
                cities={mapCities}
              />
            )}
          </div>
        </div>
      )}
    />
  </div>
);

}

/* --- Inner pie without header (for Cities) --- */
function LocationPieInner({ data, refreshKey }) {
  return <LocationPie data={data} hideHeader refreshKey={refreshKey} />;
}

/* --- Main pie --- */
function LocationPie({ title, data, hideHeader = false, onPointClick, refreshKey }) {
  const chartRef = useRef(null);
  
  // Refresh chart when refreshKey changes
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.refresh();
  }, [refreshKey]);

  const storedUser = useMemo(
    () => JSON.parse(localStorage.getItem("adm_panel_user") || "{}"),
    []
  );
  const isDark = !!storedUser.darkmode;
  
  const textColor = isDark ? "#e5e7eb" : "#0f172a";
  const connectorColor = isDark ? "#475569" : "#cbd5e1";

  return (
    <div className="location-chart-box-inner">
      {!hideHeader && (
        <div className="location-box-header">
          <h4>{title}</h4>
        </div>
      )}

      {!data || data.length === 0 ? (
        <div className="location-empty">No data</div>
      ) : (
        <div className="location-pie-wrap">
          <AccumulationChartComponent
          legendRender={(args) => {
            args.text = displayCountry(args.text);
          }}
            ref={chartRef}
            width="100%"
            height="420px"
            background="transparent"
            legendSettings={{
              visible: true,
              position: "Right",
              alignment: "Center",
              textStyle: {
                size: "12px",
                color: textColor,
                fontWeight: "500",
                fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              },
            }}
            tooltip={{ enable: true }}
            tooltipRender={(args) => {
              const label = displayCountry(args.point.x);
              args.text = `${label} : ${formatPercent(args.point.y, 1)}`;
            }}
            pointRender={(args) => {
              if (onPointClick && args.point) {
                args.fill = args.fill || PIE_COLORS[args.point.index % PIE_COLORS.length];
              }
            }}
            pointClick={(args) => {
              if (onPointClick && args.point) {
                onPointClick(args.point.x);
              }
            }}
          >
            <Inject
              services={[
                PieSeries,
                AccumulationTooltip,
                AccumulationLegend,
              ]}
            />

            <AccumulationSeriesCollectionDirective>
              <AccumulationSeriesDirective
                dataSource={data}
                xName="key"
                yName="value"
                type="Pie"
                radius="78%"
                explode
                explodeOffset="4%"
                palettes={PIE_COLORS}
                dataLabel={{
                  visible: true,
                  position: "Outside",
                  name: "value",
                  font: {
                    fontWeight: "600",
                    size: "12px",
                    color: textColor,
                    fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                  },
                  connectorStyle: {
                    length: "14px",
                    width: 1,
                    color: connectorColor,
                  },
                  enableSmartLabels: true,
                }}
                dataLabelRender={(args) => {
                  args.text = displayCountry(args.text);
                }}
              />
            </AccumulationSeriesCollectionDirective>
          </AccumulationChartComponent>
        </div>
      )}
    </div>
  );
}
